/**
 * The flow state machine: one person, one video application, from "hello" to "thank you".
 *
 * Every screen reads from here and every step writes back, so the router can always answer
 * one question — how far has this person actually got? — and put them exactly there.
 *
 * Actions never throw. They return `true` on success and `false` on failure, and set
 * {@link useApplicationStore.error} to a warm, plain message a screen can announce through
 * `aria-live`. That keeps six different views from each inventing their own error handling.
 */

import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { defineStore } from 'pinia';
import {
  isProcessingApplicationStatus,
  isTerminalApplicationStatus,
  type ApplicationStatus,
  type ContactDetails,
  type QuestionSnapshotItem,
} from '@altamont/shared';

import {
  acceptConsent,
  createApplication,
  deleteResume,
  fetchApplicationState,
  friendlyErrorMessage,
  startQuestions,
  submitApplication,
  updateInterests,
  uploadResume as uploadResumeRequest,
} from '../lib/api';
import { clearSession, loadSession, saveSession } from '../lib/session';

/**
 * The steps of the flow, in order (START.md §4). These are the `meta.step` values on the
 * routes, and the number persisted to `sessionStorage`.
 */
export const STEPS = {
  welcome: 0,
  info: 1,
  interests: 2,
  consent: 3,
  introduction: 4,
  questions: 5,
  closing: 6,
  done: 7,
} as const;

/** One of the values of {@link STEPS}. */
export type FlowStep = (typeof STEPS)[keyof typeof STEPS];

/**
 * The two ways back to a candidate, cleaned and storage-ready — exactly what
 * `validateContactDetails` returns, minus the two search fields the server derives for itself.
 * Either may be the empty string, meaning the candidate did not give it, and never both.
 */
export type CandidateContact = Pick<ContactDetails, 'email' | 'phone'>;

export const useApplicationStore = defineStore('application', () => {
  /* ---------------------------------------------------------------------------------------- *
   * State
   * ---------------------------------------------------------------------------------------- */

  /** Firestore id, once the application exists. `null` before the name and contact are saved. */
  const applicationId: Ref<string | null> = ref(null);
  /** Full name, as typed. */
  const candidateName = ref('');
  /**
   * Email address as the server stores it, or `''` when this candidate gave only a number. One of
   * the two ways back to this person, and at least one of them is always here (START.md §6).
   */
  const email = ref('');
  /** Phone number in E.164, or `''` when this candidate gave only an address (START.md §6). */
  const phone = ref('');
  /** Ids of the chosen work-interest categories. */
  const interestCategoryIds: Ref<string[]> = ref([]);
  /** Labels echoed back by the server for the chosen categories. */
  const interestLabels: Ref<string[]> = ref([]);
  /** True once a resume has been stored. */
  const hasResume = ref(false);
  /** True once the disclosure has been explicitly agreed to. */
  const consentAccepted = ref(false);
  /** The frozen question set. Empty until the questions begin (START.md §12). */
  const questionSnapshot: Ref<QuestionSnapshotItem[]> = ref([]);
  /** Question ids that already have an accepted, uploaded answer. */
  const answeredQuestionIds: Ref<string[]> = ref([]);
  /** Which question is on screen, as an index into {@link questionSnapshot}. */
  const currentQuestionIndex = ref(0);
  /** Server-side status of the application. */
  const status: Ref<ApplicationStatus> = ref('draft');
  /** Set once the submit has been acknowledged, so the confirmation screen stays reachable. */
  const submittedAt: Ref<string | null> = ref(null);
  /** True while the final submit is in flight. */
  const submitting = ref(false);
  /** True while any other action is in flight, for button spinners. */
  const saving = ref(false);
  /** Friendly message for the last failure, or `null`. Cleared at the start of every action. */
  const error: Ref<string | null> = ref(null);
  /** True once {@link restore} has run, whether or not it found anything. */
  const restored = ref(false);

  let restorePromise: Promise<boolean> | null = null;

  /* ---------------------------------------------------------------------------------------- *
   * Getters
   * ---------------------------------------------------------------------------------------- */

  /** How many questions this person was given. */
  const totalQuestions = computed(() => questionSnapshot.value.length);

  /** The question on screen, or `null` before the snapshot exists. */
  const currentQuestion: ComputedRef<QuestionSnapshotItem | null> = computed(
    () => questionSnapshot.value[currentQuestionIndex.value] ?? null,
  );

  /** How many of the frozen questions have an accepted answer. */
  const answeredCount = computed(
    () =>
      questionSnapshot.value.filter((question) =>
        answeredQuestionIds.value.includes(question.questionId),
      ).length,
  );

  /** "Question 2 of 5" (START.md §13). Empty string before there are any questions. */
  const progressLabel = computed(() =>
    totalQuestions.value === 0
      ? ''
      : `Question ${currentQuestionIndex.value + 1} of ${totalQuestions.value}`,
  );

  /** Every question is required, so this is the gate on the closing screen (START.md §17). */
  const allQuestionsAnswered = computed(
    () => totalQuestions.value > 0 && answeredCount.value === totalQuestions.value,
  );

  /** Index of the first question still without an answer, or `-1` when they are all done. */
  const nextUnansweredIndex = computed(() =>
    questionSnapshot.value.findIndex(
      (question) => !answeredQuestionIds.value.includes(question.questionId),
    ),
  );

  /** True once the application has left the person's hands: submitted, processing or finished. */
  const isSubmitted = computed(
    () =>
      submittedAt.value !== null ||
      isProcessingApplicationStatus(status.value) ||
      isTerminalApplicationStatus(status.value),
  );

  /**
   * The furthest step this person has genuinely completed the prerequisites for. The router
   * guard uses it to bounce a deep link back to somewhere real, and it is what gets persisted.
   *
   * `info` is the floor: the welcome screen always leads there, application or not.
   */
  const furthestStep: ComputedRef<number> = computed(() => {
    if (!applicationId.value) return STEPS.info;
    if (isSubmitted.value) return STEPS.done;
    if (interestCategoryIds.value.length === 0) return STEPS.interests;
    if (!consentAccepted.value) return STEPS.consent;
    if (totalQuestions.value === 0) return STEPS.introduction;
    if (!allQuestionsAnswered.value) return STEPS.questions;
    return STEPS.closing;
  });

  /* ---------------------------------------------------------------------------------------- *
   * Persistence
   * ---------------------------------------------------------------------------------------- */

  function persist(): void {
    if (!applicationId.value) return;
    saveSession({
      applicationId: applicationId.value,
      step: furthestStep.value,
      answeredQuestionIds: [...answeredQuestionIds.value],
    });
  }

  /* ---------------------------------------------------------------------------------------- *
   * Actions
   * ---------------------------------------------------------------------------------------- */

  function begin(busy: Ref<boolean>): void {
    error.value = null;
    busy.value = true;
  }

  function fail(cause: unknown, busy: Ref<boolean>): false {
    console.error('[altamont] application action failed', cause);
    error.value = friendlyErrorMessage(cause);
    busy.value = false;
    return false;
  }

  /**
   * Create the application from a name and at least one way back to the candidate (START.md §6).
   *
   * `contact` is what `validateContactDetails` returned, which is the thing that enforces the
   * at-least-one rule — the server runs it again on the body. Both fields are sent every time,
   * empty one included: `''` is how this codebase spells "not given" for these two, and the
   * server reads it the same way.
   */
  async function startApplication(name: string, contact: CandidateContact): Promise<boolean> {
    begin(saving);
    try {
      const created = await createApplication({
        candidateName: name,
        email: contact.email,
        phone: contact.phone,
      });
      applicationId.value = created.applicationId;
      status.value = created.status;
      candidateName.value = name;
      email.value = contact.email;
      phone.value = contact.phone;
      saving.value = false;
      persist();
      return true;
    } catch (cause) {
      return fail(cause, saving);
    }
  }

  /** Save the kinds of work someone is interested in (START.md §7). */
  async function saveInterests(ids: string[]): Promise<boolean> {
    if (!applicationId.value) return fail(missingApplication(), saving);
    begin(saving);
    try {
      const result = await updateInterests(applicationId.value, { interestCategoryIds: ids });
      interestCategoryIds.value = [...ids];
      interestLabels.value = [...result.interestLabels];
      saving.value = false;
      persist();
      return true;
    } catch (cause) {
      return fail(cause, saving);
    }
  }

  /** Upload a resume. `onProgress` receives a fraction from 0 to 1 for the progress bar. */
  async function uploadResume(
    file: File,
    onProgress?: (fraction: number) => void,
  ): Promise<boolean> {
    if (!applicationId.value) return fail(missingApplication(), saving);
    begin(saving);
    try {
      await uploadResumeRequest(applicationId.value, file, onProgress);
      hasResume.value = true;
      saving.value = false;
      persist();
      return true;
    } catch (cause) {
      return fail(cause, saving);
    }
  }

  /** Remove an uploaded resume. */
  async function removeResume(): Promise<boolean> {
    if (!applicationId.value) return fail(missingApplication(), saving);
    begin(saving);
    try {
      await deleteResume(applicationId.value);
      hasResume.value = false;
      saving.value = false;
      persist();
      return true;
    } catch (cause) {
      return fail(cause, saving);
    }
  }

  /** Record explicit agreement to a specific disclosure version (START.md §8). */
  async function giveConsent(version: string): Promise<boolean> {
    if (!applicationId.value) return fail(missingApplication(), saving);
    begin(saving);
    try {
      await acceptConsent(applicationId.value, { accepted: true, consentVersion: version });
      consentAccepted.value = true;
      saving.value = false;
      persist();
      return true;
    } catch (cause) {
      return fail(cause, saving);
    }
  }

  /**
   * Freeze the question set and start the question portion (START.md §12).
   *
   * Once a snapshot exists it is never replaced: whatever HR changes afterwards must not
   * reach an application already in progress.
   */
  async function beginQuestions(): Promise<boolean> {
    if (questionSnapshot.value.length > 0) return true;
    if (!applicationId.value) return fail(missingApplication(), saving);
    begin(saving);
    try {
      const result = await startQuestions(applicationId.value);
      questionSnapshot.value = [...result.questionSnapshot];
      currentQuestionIndex.value = Math.max(0, nextUnansweredIndex.value);
      saving.value = false;
      persist();
      return true;
    } catch (cause) {
      return fail(cause, saving);
    }
  }

  /** Note that a question now has an accepted answer. Idempotent — a retake must not double-count. */
  function markAnswered(questionId: string): void {
    if (!answeredQuestionIds.value.includes(questionId)) {
      answeredQuestionIds.value = [...answeredQuestionIds.value, questionId];
    }
    persist();
  }

  /** Move to a question by index. Out-of-range values are clamped rather than rejected. */
  function goToQuestion(index: number): void {
    if (totalQuestions.value === 0) {
      currentQuestionIndex.value = 0;
      return;
    }
    const safe = Number.isFinite(index) ? Math.floor(index) : 0;
    currentQuestionIndex.value = Math.min(Math.max(safe, 0), totalQuestions.value - 1);
  }

  /** Submit the finished application (START.md §18). Refuses while any question is unanswered. */
  async function submit(): Promise<boolean> {
    if (!applicationId.value) return fail(missingApplication(), submitting);
    if (!allQuestionsAnswered.value) {
      error.value = 'There is still a question waiting for an answer.';
      return false;
    }
    begin(submitting);
    try {
      const result = await submitApplication(applicationId.value);
      status.value = result.status;
      submittedAt.value = result.submittedAt;
      submitting.value = false;
      persist();
      return true;
    } catch (cause) {
      return fail(cause, submitting);
    }
  }

  /** Forget everything, in memory and in this tab's storage, and start over. */
  function reset(): void {
    applicationId.value = null;
    candidateName.value = '';
    email.value = '';
    phone.value = '';
    interestCategoryIds.value = [];
    interestLabels.value = [];
    hasResume.value = false;
    consentAccepted.value = false;
    questionSnapshot.value = [];
    answeredQuestionIds.value = [];
    currentQuestionIndex.value = 0;
    status.value = 'draft';
    submitting.value = false;
    saving.value = false;
    submittedAt.value = null;
    error.value = null;
    clearSession();
  }

  /**
   * Pick the flow back up after a refresh: read this tab's session, then re-sync with the
   * server, which is the only source of truth for what is actually saved.
   *
   * Anything unexpected — a corrupt payload, a deleted application, an expired session —
   * ends in a clean {@link reset} and a fresh start. Nobody is left stranded on a broken screen.
   */
  function restore(): Promise<boolean> {
    if (restorePromise) return restorePromise;
    restorePromise = (async (): Promise<boolean> => {
      try {
        const saved = loadSession();
        if (!saved) return false;

        const state = await fetchApplicationState(saved.applicationId);
        applicationId.value = state.id;
        candidateName.value = state.candidateName;
        status.value = state.status;
        interestCategoryIds.value = [...state.interestCategoryIds];
        hasResume.value = state.hasResume;
        consentAccepted.value = state.consentAccepted;
        questionSnapshot.value = [...state.questionSnapshot];
        answeredQuestionIds.value = [...state.answeredQuestionIds];
        currentQuestionIndex.value = Math.max(0, nextUnansweredIndex.value);
        persist();
        return true;
      } catch (cause) {
        console.warn(
          '[altamont] Could not pick up the saved video application; starting over.',
          cause,
        );
        reset();
        return false;
      } finally {
        restored.value = true;
      }
    })();
    return restorePromise;
  }

  function missingApplication(): Error {
    return new Error('No application has been started yet.');
  }

  return {
    // state
    applicationId,
    candidateName,
    email,
    phone,
    interestCategoryIds,
    interestLabels,
    hasResume,
    consentAccepted,
    questionSnapshot,
    answeredQuestionIds,
    currentQuestionIndex,
    status,
    submittedAt,
    submitting,
    saving,
    error,
    restored,
    // getters
    currentQuestion,
    totalQuestions,
    answeredCount,
    progressLabel,
    allQuestionsAnswered,
    nextUnansweredIndex,
    isSubmitted,
    furthestStep,
    // actions
    startApplication,
    saveInterests,
    uploadResume,
    removeResume,
    giveConsent,
    beginQuestions,
    markAnswered,
    goToQuestion,
    submit,
    reset,
    restore,
  };
});
